import { Injectable } from '@angular/core';
import {
  FingerprintAIO,
  FingerprintOptions
} from '@ionic-native/fingerprint-aio/ngx';
import { Platform } from '@ionic/angular';
import { BehaviorSubject } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { LogService } from '../log.service';
import { KeychainService } from '../storage/keychain.service';
import { UiHelperService } from '../ui-helper.service';

export interface Biometric {
  isReady: boolean;
  available?: boolean;
  passwordAvailable?: boolean;
  type?: {
    biometric: boolean;
    fingerprint: boolean;
    faceId: boolean;
    showOpts: any;
  };
}

@Injectable({
  providedIn: 'root'
})
export class BiometricService {
  private initialOptions: Biometric = { isReady: false };
  private options = new BehaviorSubject<Biometric>(this.initialOptions);

  constructor(
    private platform: Platform,
    private log: LogService,
    private fingerprintAIO: FingerprintAIO,
    private keychain: KeychainService,
    private ui: UiHelperService
  ) {}

  getBiometricOptions$() {
    return this.options.pipe(
      filter(options => options.isReady),
      take(1)
    );
  }

  async show(opts: FingerprintOptions) {
    let res: boolean;
    try {
      await this.fingerprintAIO.show(opts);
      res = true;
    } catch (err) {
      res = false;
      this.log.d(this, 'Unsuccessful authentication', err);
      if (this.platform.is('ios') && err.code === -101) {
        this.ui.presentAlert(
          'Permisos rechazados para usar reconocimiento facial/huella',
          'INFO'
        );
      }
    }

    return Promise.resolve(res);
  }

  async init() {
    let state;
    await this.platform.ready();
    const keychain = await this.keychain.isKeychainAvailable();
    const biometric = await this.isFingerpintAvailable();

    if (!keychain || biometric === null) {
      state = { available: false };
    } else {
      state = {
        available: true,
        type: {
          biometric: biometric === 'biometric',
          fingerprint: biometric === 'finger',
          faceId: biometric !== 'finger' && biometric !== 'biometric'
        }
      };
      state.type.showOpts = this.populateShowOpts(state);
    }

    this.options.next({ ...state, isReady: true });
  }

  private isFingerpintAvailable(): Promise<string> {
    return this.platform.is('cordova')
      ? this.fingerprintAIO.isAvailable().catch(() => null)
      : Promise.resolve(null);
  }

  async checkIfPasswordIsAvailable() {
    const password = await this.keychain.get('PASSWORD').catch(() => '');
    const passwordAvailable = !!password;

    this.options.next({
      ...this.options.getValue(),
      passwordAvailable
    });
  }

  private populateShowOpts({ type }: Biometric) {
    let showOpts;
    if (type.fingerprint) {
      showOpts = {
        title: 'Huella para MAPFRE',
        disableBackup: true,
        cancelButtonTitle: 'Cancelar'
      };
    } else if (type.biometric) {
      showOpts = {
        title: 'Huella / Reconocimiento facial para MAPFRE',
        disableBackup: true,
        cancelButtonTitle: 'Cancelar'
      };
    } else {
      showOpts = {
        title: 'Reconocimiento facial para MAPFRE',
        disableBackup: true
      };
    }

    return showOpts;
  }
}
